
import { NgView } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { ApiService } from "@app/services/api.service";
import { QuoteService } from "@app/services/quote.service";
import { SearchService } from "@app/services/search.service";
import { ISearchScope, SearchController } from "@app/views/search.view";
import { eFieldType, eFilterControl, eFilterSource, eFilterType, IFilter, IQuote, IQuoteProduct, ISavedSearch, meta } from "@models";
import { icons } from "@tools";

export interface IConfiguredProductsScope extends ISearchScope<IQuote> {
    fieldChange: (filter: IFilter) => void;
    // getFilterControlForField: (fieldName: string) => string;

    showCopyToActiveQuoteBtn: (qp: IQuoteProduct) => boolean;
    showCopyToNewQuoteBtn: (qp: IQuoteProduct) => boolean;
    copyProductToActiveQuote: (qp: IQuoteProduct) => ng.IPromise<any>;
    copyProductToNewQuote: (qp: IQuoteProduct) => ng.IPromise<any>;
}

@NgView({
    token: Token.ConfiguredProductsView,
    inherit: [Token.SearchView],
    dependencies: [
        Token.QuoteService,
    ]
})
export class ConfiguredProductsController extends SearchController<IQuoteProduct> {

    constructor(
        public $scope: IConfiguredProductsScope,
        agg: SearchService,
        quoteService: QuoteService
    ) {

        super(
            $scope,
            agg
        );
        agg.drawerService.setHelpUrl("Quotes");
        agg.drawerService.pageType = loc.configuredproducts;
        agg.drawerService.pageIcon = icons.quote;

        $scope.binding.search.modelType = meta.QuoteProduct.$name;
        $scope.savedSearchEnabled = true;
        this.refreshSavedSearches();
        $scope.modelTypes = [
            { type: meta.Quote.$name, label: "Quotes", state: "kb.quotes" },
            { type: meta.QuoteProduct.$name, label: "Configured Products", state: "kb.configuredProducts" }
        ];

        this.massageFilters($scope.availableFilters);
        $scope.availableFilters.pushArray([
            this.getWhereFieldFilter()
        ]);

        let fieldsDb: { [name: string]: { id; name; type; } } = {};
        $scope.$watch("binding.search.filters[0].values[0]", (newVal: number, oldVal) => {
            if (newVal) {
                agg.api.configurators.searchFields(newVal).then(r => {
                    fieldsDb = {};
                    r.forEach(field => fieldsDb[field.name] = field);
                    $scope.filterSource[eFilterSource.fields] = r;
                });
            }
        });

        // $scope.getFilterControlForField = (fieldName) => {
        //    var field = fieldsDb[fieldName];
        //    var fieldType = field ? field.type : eFieldType.text;
        //    if (fieldType == eFieldType.number)
        //        return eFilterControl.numberRange;
        //    else if (fieldType == eFieldType.boolean)
        //        return eFilterControl.checkbox;
        //    else
        //        return eFilterControl.text;
        // };

        $scope.fieldChange = filter => {
            let field = fieldsDb[filter.fieldName];
            let fieldType = field ? field.type : eFieldType.text;
            filter.fieldType = fieldType;
            if (fieldType == eFieldType.number) {
                filter.control = eFilterControl.numberRange;
            } else if (fieldType == eFieldType.boolean) {
                filter.control = eFilterControl.checkbox;
            } else {
                filter.control = eFilterControl.text;
            }
        };

        $scope.showCopyToActiveQuoteBtn = (qp) => {
            /** only show this 'copy to active quote' button if we are not in the active quote, and the active quote allows modifying products */
            return (
                quoteService.quote
                && quoteService.canModifyProductsOfQuote(quoteService.quote)
                && !agg.$rootScope.isSfdc //this should never happen in CRM integration
            );
        };

        $scope.showCopyToNewQuoteBtn = (qp) => {
            return !agg.$rootScope.isSfdc; //creating a new quote in this way would break the CRM integration
        }

        $scope.copyProductToActiveQuote = (qp) => {
            return quoteService.copyProductToActiveQuote(qp.id).then(r => {
                quoteService.goToQuote();
            });
        }

        $scope.copyProductToNewQuote = qp => {
            return quoteService.copyProductToNewQuote(qp.id).then(r => {
                quoteService.goToQuote();
            });
        }


        agg.drawerService.addDrawer(icons.clone, loc.clone, () => this.clone());
        agg.drawerService.addDrawer(icons.deleter, loc.deleter, () => this.deleteItems());
    }
    public client(api: ApiService) {
        if (this.agg.$rootScope.company.useDatabaseSearch){
            return api.quoteProducts;
        } else {
            return api.quotes;
        }
    }

    public newSearch(): ISavedSearch {
        return {
            modelType: meta.QuoteProduct.$name,
            sortField: "modifiedDate",
            descending: true,
            filters: (() => {
                let filters: IFilter[] = [];
                filters.push(
                    {
                        type: eFilterType.property,
                        property: "idProduct",
                        label: "Product Type",
                        control: eFilterControl.select,
                        sourceType: eFilterSource.configurators,
                        valueField: "id",
                        labelField: "name",
                        values: [null],
                        sticky: true
                    },
                    this.getWhereFieldFilter()
                );

                filters.pushArray(this.getFiltersFromMeta(meta.QuoteProduct.$name, true));
                this.massageFilters(filters);
                return filters;
            })()
        };
    }

    public massageFilters(filters: IFilter[]) {
        if (!this.agg.$rootScope.company.useDatabaseSearch){
            // configured products is a tricky screen since we're searching for quotes, 
            // but really try to show stuff about products
            // so we need to massage the filters to add the nested property syntax
            filters.forEach(f => {
                if (f.property && !f.property.startsWith("products.")) {
                    f.property = "products." + f.property;
                }
            });
        }
    }

    public postProcessFilters(filters: IFilter[]) {
        //don't send the product type filter if they haven't selected one
        filters.removeWhere(f => f.property == "idProduct" && !f.values.first());
    }


    /**
     * the properties of the queried objec that should be returned.
     */
    public fields(): string[] {
        if (this.agg.$rootScope.company.useDatabaseSearch){
            return [
                meta.QuoteProduct.id.name,
                meta.QuoteProduct.name.name,
                meta.QuoteProduct.imagePath.name,
                meta.QuoteProduct.shortDescription.name,
                meta.QuoteProduct.price.name,
                meta.QuoteProduct.sku.name,
                meta.QuoteProduct.idQuote.name,
                meta.QuoteProduct.quote.name,
                meta.QuoteProduct.currency.name,
                meta.QuoteProduct.modifiedDate.name
            ];            
        } else {
            return [
                meta.Quote.id.name,
                meta.Quote.name.name,
                meta.Quote.description.name,
                meta.Quote.customer.name,
                meta.Quote.idCustomer.name,
                meta.Quote.contact.name,
                meta.Quote.idContact.name,
                meta.Quote.totalPrice.name,
                meta.Quote.state.name,
                meta.Quote.createdDate.name,
                meta.Quote.modifiedDate.name,
                meta.Quote.ownedBy.name,
                meta.Quote.owner.name,
                meta.Quote.currency.name
            ];
        }
    }

    public nestedFields(): string[] {
        if (this.agg.$rootScope.company.useDatabaseSearch){
            return [];            
        } else {
            return [
                meta.QuoteProduct.id.name,
                meta.QuoteProduct.name.name,
                meta.QuoteProduct.imagePath.name,
                meta.QuoteProduct.shortDescription.name,
                meta.QuoteProduct.price.name,
                meta.QuoteProduct.sku.name
            ];
        }
    }

    public getNestedPath(): string {
        if (this.agg.$rootScope.company.useDatabaseSearch){
            return null;
        } else {
            return "products";
        }
    }

    // public getRawFilter() {
    //    return { term: { isConfigured: true } };
    // }

    public getWhereFieldFilter(): IFilter {
        return {
            type: eFilterType.fieldValue,
            label: "Where Field",
            fieldName: null,
            control: eFilterControl.none,
            sourceType: eFilterSource.fields,
            values: [null]
        };
    }

    public getExtraFilters(): IFilter[] {
        if (this.agg.$rootScope.company.useDatabaseSearch){
            return [
                {
                    property: meta.QuoteProduct.isConfigured.name,
                    values: [true]
                } as IFilter
            ];
        } else {
            return [];
        }
       
    }
}
